import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import Cookies from 'js-cookie';
import Button from '../../../shared/components/ui/Button';
import { submitKeepLeadActiveQualification } from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import StageQualificationModal from './StageQualificationModal';

export default function NurtureRecommendationBanner({
  leadId,
  leadName,
  currentPstatus = null,
  nurtureRecommendation = null,
  onMoveToNurture,
  onScheduleFollowUp,
  onAfterKeepActive
}) {
  const queryClient = useQueryClient();
  const [keepActiveOpen, setKeepActiveOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isSalesExecutive =
    Cookies.get('userVertical') === 'Sales' && Cookies.get('userDesignationLevel') === 'Low';
  const isHotLead = String(currentPstatus || '').toLowerCase() === 'hot';

  const nurture = nurtureRecommendation;
  if (!nurture?.recommended) return null;

  const summaryText = (nurture.summary || '').trim();
  const labelText = (nurture.label || '').trim();
  const uniqueReasons = (nurture.reasons || []).filter((reason) => {
    const r = String(reason || '').trim();
    if (!r) return false;
    if (summaryText && r === summaryText) return false;
    if (labelText && r === labelText) return false;
    return true;
  });

  const severityClass =
    nurture.severity === 'red'
      ? 'border-red-300 bg-red-50 text-red-900'
      : nurture.severity === 'orange'
        ? 'border-orange-300 bg-orange-50 text-orange-900'
        : 'border-yellow-300 bg-yellow-50 text-yellow-900';

  const handleKeepActive = async (payload) => {
    try {
      setSubmitting(true);
      await submitKeepLeadActiveQualification(leadId, payload);
      toast.success('Lead kept active — reason recorded');
      setKeepActiveOpen(false);
      if (leadId) {
        queryClient.invalidateQueries({ queryKey: ['aiLeadInsight', leadId] });
        queryClient.invalidateQueries({ queryKey: ['aiLeadWhatsappTemplates', leadId] });
        queryClient.invalidateQueries({ queryKey: ['stageQualificationProfile', leadId] });
        queryClient.invalidateQueries({ queryKey: ['latestStageQualification', leadId] });
      }
      onAfterKeepActive?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not save');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className={`mb-4 rounded-xl border p-4 ${severityClass}`}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{nurture.label || 'Recommended for Nurture'}</p>
            <p className="mt-1 text-sm opacity-90">{nurture.summary}</p>
            {uniqueReasons.length ? (
              <ul className="mt-2 list-disc pl-5 text-sm opacity-90">
                {uniqueReasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            ) : null}
            {isHotLead && nurture.severity !== 'yellow' ? (
              <p className="mt-2 text-xs font-medium opacity-90">
                Hot leads flagged for nurture require manager awareness if you keep them active.
              </p>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" onClick={() => onMoveToNurture?.()}>
                Move to Paused / Nurture
              </Button>
              <Button type="button" displayType="secondary" onClick={() => setKeepActiveOpen(true)}>
                Keep as Warm/Hot
              </Button>
              <Button type="button" displayType="secondary" onClick={() => onScheduleFollowUp?.()}>
                Schedule follow-up
              </Button>
            </div>
          </div>
        </div>
      </div>

      <StageQualificationModal
        open={keepActiveOpen}
        onClose={() => setKeepActiveOpen(false)}
        leadId={leadId}
        transition="keep_active"
        leadName={leadName}
        currentPstatus={currentPstatus}
        requireManagerAck={isSalesExecutive && isHotLead}
        onSubmit={handleKeepActive}
        isSubmitting={submitting}
      />
    </>
  );
}
